import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { PageHero, FinalCta } from "../components/Sections";
import { EXPERIENCE_BRANDS, PROCESS } from "../data/site";

const About = () => {
    useEffect(() => {
        document.title = "About ALBL Communications | Brand-Marketing Experience, Narmadapuram";
    }, []);

    return (
        <main>
            <PageHero
                eyebrow="About ALBL"
                title="WHY ALBL EXISTS."
                sub="A brand, marketing and sales growth partner — built on big-brand experience, focused on Central Madhya Pradesh."
            />

            <section className="bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <p className="font-serif-accent italic text-xl sm:text-2xl text-slate-800 leading-relaxed">
                                Local businesses shouldn&apos;t have to choose between expensive
                                corporate agencies and generic digital marketing packages.
                            </p>
                            <p className="mt-6 text-slate-600 text-base sm:text-lg leading-relaxed">
                                They deserve access to serious marketing thinking — adapted to their
                                market, their customers and their business reality.
                            </p>
                            <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
                                With professional experience across brands such as LG, Haier and
                                Bisleri, ALBL brings structured brand-marketing thinking to businesses
                                that want to grow.
                            </p>
                            <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
                                We don&apos;t start with Instagram posts or marketing packages. We start
                                with the business — what you sell, who should buy it, why they should
                                choose you, where you&apos;re losing customers, and what is stopping
                                growth. Then we build the marketing around those answers.
                            </p>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-5">
                        <Reveal delay={150}>
                            <div className="border border-slate-200 bg-[#F8FAFC] p-8 sm:p-10">
                                <Eyebrow>Professional experience across</Eyebrow>
                                <div className="mt-6 grid grid-cols-3 gap-3">
                                    {EXPERIENCE_BRANDS.map((b) => (
                                        <div
                                            key={b}
                                            className="border border-slate-200 bg-white py-6 flex items-center justify-center font-display text-xl font-bold tracking-widest text-slate-800"
                                        >
                                            {b}
                                        </div>
                                    ))}
                                </div>
                                <p className="mt-6 text-sm text-slate-500 leading-relaxed">
                                    From campaign strategy and consumer communication to retail
                                    visibility, trade marketing, digital campaigns and market
                                    execution — the discipline behind serious brands, applied to local
                                    businesses.
                                </p>
                                <p className="mt-4 text-xs text-slate-400 leading-relaxed">
                                    Brand names indicate professional brand-marketing experience only.
                                    ALBL is not an official partner, representative or agency of these
                                    companies.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-[#0D131F] text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28">
                    <Reveal>
                        <Eyebrow dark>How we engage</Eyebrow>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1] mt-5">
                            NO COOKIE-CUTTER MARKETING.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                        {PROCESS.map((p) => (
                            <div key={p.num} className="bg-[#0D131F] p-8">
                                <span className="font-serif-accent italic text-3xl text-[#C5A059]">{p.num}</span>
                                <h3 className="font-display text-xl font-bold uppercase tracking-tight mt-4">
                                    {p.title}
                                </h3>
                                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.text}</p>
                            </div>
                        ))}
                    </div>
                    <Reveal delay={150}>
                        <Link
                            to="/contact?intent=audit"
                            data-testid="about-cta-audit-button"
                            className="btn-sharp mt-12 inline-flex items-center gap-2 bg-[#C5A059] text-[#0D131F] text-xs font-bold tracking-[0.15em] uppercase px-8 py-4"
                        >
                            Start with a Growth Audit <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <FinalCta />
        </main>
    );
};

export default About;
